import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
} from "react-bootstrap";
import logo from "../../../htdocs/images/logo.png";
import "./LandingPage.css";

/* Content is static marketing copy — kept as data so sections stay lean. */

const FEATURES = [
  {
    icon: "mdi-calendar-clock",
    title: "Asistencia y calificación",
    text: "El docente pasa lista, califica con estrellas y firma digitalmente — todo en una sola pantalla, incluso sin internet.",
  },
  {
    icon: "mdi-wallet-outline",
    title: "Saldos de horas",
    text: "Cada clase descuenta horas automáticamente del paquete del estudiante. Sin planillas, sin cuentas a mano.",
  },
  {
    icon: "mdi-cash-register",
    title: "Pagos y cartera",
    text: "Registra pagos de paquetes, detecta la cartera vencida y sabe quién debe renovar antes de que deje de venir.",
  },
  {
    icon: "mdi-cash-multiple",
    title: "Honorarios docentes",
    text: "Las horas dictadas se acumulan solas. Liquida los honorarios de cada docente en un par de clics.",
  },
  {
    icon: "mdi-tag-multiple",
    title: "Temas y continuidad",
    text: "Catálogo de temas por disciplina y alertas de repetición: cada estudiante avanza sin volver a ver lo mismo.",
  },
  {
    icon: "mdi-map-marker-outline",
    title: "Multi-sede",
    text: "Administra varias sedes desde una sola cuenta, con agenda y reportes separados por ubicación.",
  },
];

const ROLES = [
  {
    icon: "mdi-view-dashboard-outline",
    title: "Propietario",
    text: "Panel con ingresos, cartera y estudiantes activos. Configura paquetes, tarifas, sedes y políticas.",
  },
  {
    icon: "mdi-calendar-clock",
    title: "Docente",
    text: "Su agenda, sus clases y sus horas. Programa, dicta, califica y ve cuánto le van a liquidar.",
  },
  {
    icon: "mdi-book-open-page-variant",
    title: "Estudiante",
    text: "Agenda clases, consulta su saldo de horas, sus notas y sus pagos desde el celular.",
  },
];

const PLANS = [
  {
    name: "Piloto",
    price: "$0",
    period: "para siempre",
    students: "15 estudiantes activos",
    sedes: "1 sede",
    featured: false,
    trial: false,
  },
  {
    name: "Emprende",
    price: "$79.000",
    period: "COP/mes + IVA",
    students: "60 estudiantes activos",
    sedes: "1 sede",
    featured: false,
    trial: true,
  },
  {
    name: "Academia",
    price: "$179.000",
    period: "COP/mes + IVA",
    students: "200 estudiantes activos",
    sedes: "2 sedes",
    featured: true,
    trial: true,
  },
  {
    name: "Cadena",
    price: "$399.000",
    period: "COP/mes + IVA",
    students: "600 estudiantes activos",
    sedes: "Sedes ilimitadas",
    featured: false,
    trial: true,
  },
];

/** "Ya tengo cuenta" — asks for the academy slug and routes to its login. */
function LoginBySlug() {
  const [slug, setSlug] = useState("");
  const navigate = useNavigate();

  const go = (e) => {
    e.preventDefault();
    const clean = slug.trim().toLowerCase().replace(/\s+/g, "-");
    if (clean) navigate(`/${clean}/login`);
  };

  return (
    <Form onSubmit={go} className="landing-login-slug">
      <Form.Label className="small text-muted mb-1">
        ¿Ya tienes cuenta? Escribe el identificador de tu academia:
      </Form.Label>
      <InputGroup>
        <Form.Control
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="mi-academia"
          aria-label="Identificador de tu academia"
        />
        <Button type="submit" variant="outline-primary">
          Ingresar
        </Button>
      </InputGroup>
    </Form>
  );
}

function LandingPage() {
  return (
    <div className="landing">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="landing-header">
        <Container className="d-flex justify-content-between align-items-center py-3">
          <img src={logo} alt="Diezlar" height={26} />
          <Link to="/registro-academia" className="btn btn-primary">
            Crear mi academia
          </Link>
        </Container>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="landing-hero">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={7}>
              <Badge bg="light" text="dark" className="mb-3 landing-chip">
                Para academias de música, idiomas, danza y más
              </Badge>
              <h1 className="landing-title">
                El control total de tu academia,
                <br />
                <span className="text-gradient">en una sola app.</span>
              </h1>
              <p className="landing-sub">
                Asistencia, calificaciones, saldos de horas, pagos y honorarios
                docentes — sin cuadernos, sin planillas regadas y sin perder
                ninguna renovación.
              </p>
              <div className="d-flex flex-wrap gap-2 mb-4">
                <Link
                  to="/registro-academia"
                  className="btn btn-primary btn-lg landing-cta"
                >
                  Crea tu academia gratis
                </Link>
                <a href="#planes" className="btn btn-outline-secondary btn-lg">
                  Ver planes
                </a>
              </div>
              <LoginBySlug />
            </Col>
            <Col lg={5}>
              {/* Stylized product mock — pure CSS, no screenshots to maintain */}
              <div className="landing-mock shadow">
                <div className="landing-mock-bar">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
                <div className="landing-mock-body">
                  <div className="landing-mock-row">
                    <span className="fw-semibold">Robert Mondragón</span>
                    <span className="badge-present">✓ Presente</span>
                  </div>
                  <div className="landing-mock-stars">★★★★☆</div>
                  <div className="landing-mock-row small text-muted">
                    <span>Pasado continuo · 1 h</span>
                    <span>Saldo: 306 h</span>
                  </div>
                  <div className="landing-mock-sign">Firma del docente ✍</div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section className="landing-section">
        <Container>
          <h2 className="landing-h2 text-center">
            Todo lo que hoy llevas a mano
          </h2>
          <p className="text-muted text-center mb-4">
            No importa si hoy usas cuaderno, Excel, WhatsApp o tarjetas de
            cartón — Diezlar digitaliza la operación diaria de tu academia.
          </p>
          <Row className="g-3">
            {FEATURES.map((f) => (
              <Col md={6} lg={4} key={f.title}>
                <Card className="h-100 border-0 shadow-sm landing-feature">
                  <Card.Body>
                    <div className="landing-feature-icon">
                      <i className={`mdi ${f.icon}`} aria-hidden="true" />
                    </div>
                    <h5 className="mt-3 mb-2">{f.title}</h5>
                    <p className="text-muted small mb-0">{f.text}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── Roles ───────────────────────────────────────────────────────── */}
      <section className="landing-section landing-section-alt">
        <Container>
          <h2 className="landing-h2 text-center">Una app, tres experiencias</h2>
          <p className="text-muted text-center mb-4">
            Cada rol ve exactamente lo que necesita — nada más.
          </p>
          <Row className="g-3 justify-content-center">
            {ROLES.map((r) => (
              <Col md={4} key={r.title}>
                <Card className="h-100 border-0 shadow-sm text-center landing-role">
                  <Card.Body className="p-4">
                    <div className="landing-role-icon mx-auto">
                      <i className={`mdi ${r.icon}`} aria-hidden="true" />
                    </div>
                    <h5 className="mt-3">{r.title}</h5>
                    <p className="text-muted small mb-0">{r.text}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────────── */}
      <section className="landing-section" id="planes">
        <Container>
          <h2 className="landing-h2 text-center">Planes simples, en pesos</h2>
          <p className="text-muted text-center mb-4">
            Empieza gratis y crece cuando tu academia crezca. Anual: paga 10
            meses y recibe 12.
          </p>
          <Row className="g-3 justify-content-center">
            {PLANS.map((p) => (
              <Col sm={6} lg={3} key={p.name}>
                <Card
                  className={`h-100 text-center landing-plan ${
                    p.featured ? "landing-plan-featured shadow" : "border-0 shadow-sm"
                  }`}
                >
                  <Card.Body className="p-4 d-flex flex-column">
                    {p.featured && (
                      <Badge bg="primary" className="landing-plan-badge">
                        Recomendado
                      </Badge>
                    )}
                    <h5 className="mb-1">{p.name}</h5>
                    <div className="landing-plan-price">{p.price}</div>
                    <div className="text-muted small mb-2">{p.period}</div>
                    <div className="mb-3" style={{ minHeight: 24 }}>
                      {p.trial && (
                        <span className="landing-plan-trial">
                          <i className="mdi mdi-gift-outline me-1" aria-hidden="true" />
                          21 días de prueba gratis
                        </span>
                      )}
                    </div>
                    <ul className="list-unstyled small text-muted flex-grow-1">
                      <li className="mb-1">{p.students}</li>
                      <li>{p.sedes}</li>
                    </ul>
                    <Link
                      to={`/registro-academia?plan=${encodeURIComponent(p.name)}`}
                      className={`btn w-100 ${
                        p.featured ? "btn-primary" : "btn-outline-primary"
                      }`}
                    >
                      Empezar
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <p className="text-center text-muted small mt-3 mb-0">
            ¿Más sedes, más estudiantes o requisitos especiales? Hay bloques de
            capacidad y planes a la medida.
          </p>
        </Container>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────── */}
      <section className="landing-final">
        <Container className="text-center">
          <h2 className="landing-h2 text-white mb-2">
            Menos horas administrando, más horas dictando clase.
          </h2>
          <p className="text-white-50 mb-4">
            Crea tu academia en menos de 2 minutos. Sin tarjeta de crédito.
          </p>
          <Link
            to="/registro-academia"
            className="btn btn-light btn-lg landing-cta"
          >
            Crea tu academia gratis
          </Link>
        </Container>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <Container className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-4">
          <span className="text-muted small">
            ©{" "}
            <a href="https://cvalenciait.com" target="_blank" rel="noreferrer">
              cvalenciait.com
            </a>{" "}
            — hecho para academias de Colombia y LatAm.
          </span>
          <Link to="/politica-privacidad" className="small">
            Política de privacidad
          </Link>
        </Container>
      </footer>
    </div>
  );
}

export { LandingPage };
